import { useState, useRef, useEffect } from 'react'
import styles from './MultiSelectDropdown.module.css'

export default function MultiSelectDropdown({ 
    label, 
    options = [], 
    selectedValues = [], 
    onChange, 
    placeholder = "Select options...",
    searchPlaceholder = "Search...",
    className = ""
}) {
    const [isOpen, setIsOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef(null)
    const searchInputRef = useRef(null)

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
                setSearchTerm('')
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [isOpen])

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase())
    )

    // Get selected option names for display
    const getSelectedNames = () => {
        const selectedOptions = options.filter(option => 
            selectedValues.includes(option.id)
        )
        return selectedOptions.map(option => option.name)
    }

    // Handle option selection
    const handleOptionSelect = (optionId) => {
        let newSelectedValues
        if (selectedValues.includes(optionId)) {
            // Remove if already selected
            newSelectedValues = selectedValues.filter(id => id !== optionId)
        } else {
            // Add if not selected
            newSelectedValues = [...selectedValues, optionId]
        }
        onChange(newSelectedValues)
    }

    // Handle clear all
    const handleClearAll = (e) => {
        e.stopPropagation()
        onChange([])
    }

    // Handle select all filtered
    const handleSelectAll = (e) => {
        e.stopPropagation()
        const filteredIds = filteredOptions.map(option => option.id)
        const newSelectedValues = [...new Set([...selectedValues, ...filteredIds])]
        onChange(newSelectedValues)
    }

    const selectedNames = getSelectedNames()
    const displayText = selectedNames.length === 0 
        ? placeholder 
        : selectedNames.length === 1 
            ? selectedNames[0]
            : `${selectedNames.length} selected`

    return (
        <div className={`${styles.multiSelectContainer} ${className}`} ref={dropdownRef}>
            {label && <label className={styles.label}>{label}:</label>}
            
            <div 
                className={`${styles.multiSelectTrigger} ${isOpen ? styles.open : ''}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={styles.displayText}>
                    {displayText}
                </span>
                <span className={styles.arrow}>
                    {isOpen ? '▲' : '▼'}
                </span>
            </div>

            {isOpen && (
                <div className={styles.multiSelectDropdown}>
                    {/* Search Input */}
                    <div className={styles.searchContainer}>
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={searchPlaceholder}
                            className={styles.searchInput}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    {/* Actions */}
                    <div className={styles.actions}>
                        <button 
                            type="button"
                            onClick={handleSelectAll}
                            className={styles.actionButton}
                            disabled={filteredOptions.length === 0}
                        >
                            Select All ({filteredOptions.length})
                        </button>
                        <button 
                            type="button"
                            onClick={handleClearAll}
                            className={styles.actionButton}
                            disabled={selectedValues.length === 0}
                        >
                            Clear ({selectedValues.length})
                        </button>
                    </div>

                    {/* Options List */}
                    <div className={styles.optionsList}>
                        {filteredOptions.length === 0 ? (
                            <div className={styles.noOptions}>
                                {searchTerm ? 'No matching options' : 'No options available'}
                            </div>
                        ) : (
                            filteredOptions.map((option) => (
                                <div
                                    key={option.id}
                                    className={`${styles.option} ${
                                        selectedValues.includes(option.id) ? styles.selected : ''
                                    } ${!option.active ? styles.inactive : ''}`}
                                    onClick={() => handleOptionSelect(option.id)}
                                >
                                    <input
                                        type="checkbox"
                                        checked={selectedValues.includes(option.id)}
                                        onChange={() => {}} // Handled by parent click
                                        className={styles.checkbox}
                                    />
                                    <span className={styles.optionText}>
                                        {option.name}
                                        {option.type && <span className={styles.optionType}> ({option.type})</span>}
                                        {!option.active && <span className={styles.inactiveLabel}> (Inactive)</span>}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}